import React from 'react';

import TextButton from '~/core/atoms/Buttons/TextButton';
import { ArrowButton } from '~/core/molecules/Table/TablePagination/ArrowButton';
import cn from '~/utils/cn';

interface NavigationControlsProps {
  currentPage: number;
  totalPages: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onPageChange: (pageNumber: number) => void;
}

const NavigationControls: React.FC<NavigationControlsProps> = ({
  currentPage,
  totalPages,
  canPreviousPage,
  canNextPage,
  onPreviousPage,
  onNextPage,
  onPageChange,
}) => {
  const getVisiblePageNumbers = () => {
    // "delta" represents the amount of pages to show around the current page - if page 6 is selected, present 2 pages before and after
    // (1)...(4)(5)(-6-)(7)(8)...(10)
    const delta = 2;
    const rangeWithDots = [];

    // Generate range of page numbers around current page
    const start = Math.max(2, currentPage - delta);
    const end = Math.min(totalPages - 1, currentPage + delta);
    const range = Array.from({ length: end - start + 1 }, (_, i) => start + i);

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  return (
    <div className="flex items-center gap-2">
      {/* Previous button */}
      <ArrowButton onClick={onPreviousPage} direction="left" disabled={!canPreviousPage} />

      {/* Page numbers */}
      <div className="flex items-center gap-1">
        {getVisiblePageNumbers().map((page, index) => {
          if (page === '...') {
            return (
              <span key={`dots-${index}`} className="text-grey-500 px-2 py-1">
                ...
              </span>
            );
          }

          const pageNumber = page as number;
          const isCurrentPage = pageNumber === currentPage;

          return (
            <TextButton
              variant="secondary"
              key={pageNumber}
              onClick={() => onPageChange(pageNumber)}
              className={cn('text-grey-700 flex-1 shrink-0 rounded-full px-8 text-sm hover:bg-blue-100', {
                'bg-blue-200 hover:bg-black/25 hover:font-bold': isCurrentPage,
              })}
            >
              {pageNumber}
            </TextButton>
          );
        })}
      </div>

      {/* Next button */}
      <ArrowButton onClick={onNextPage} direction="right" disabled={!canNextPage} />
    </div>
  );
};

export default NavigationControls;
